import { TYRE_IDENTIFIER_FIELD } from '@wyz/types';
import { Col, Row, Flex, Icon, Modal, Text, Title, TyreInfoBox } from '@wyz/ui';
import { getTyreIdentifiers, isEmptyValue, BlobHelpers } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { useEffect, useState } from 'react';
import {
  useApplicationContext,
  useConfig,
  useLocale,
  Trans,
  useI18n,
} from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { ImageSlide } from '~/purchase/components';
import { DEFAULT_ENERGY_LABEL } from '~/purchase/helpers';
import { IQuoteItemInfo } from '~/purchase/types';

type Props = {
  item: IQuoteItemInfo;
  customer: IUserCustomer;
  onClose: () => void;
  currency: string;
};

function DetailModal({ customer, item, onClose, currency }: Props) {
  const { t } = useI18n();
  const { cdnUrl, withCdnUrl, apiUrl } = useConfig();
  const { auth } = useApplicationContext();
  const locale = useLocale();

  const [energyLabelSrc, setEnergyLabelSrc] = useState<string>(
    `data:image/png;base64,${DEFAULT_ENERGY_LABEL}`,
  );

  useEffect(() => {
    const controller = new AbortController();
    BlobHelpers.getDataUrl({
      url: `${apiUrl}/offers/api/assets/tyres/${item.tyre.ean}/labels/medium`,
      token: auth.user?.access_token ?? '',
      signal: controller.signal,
    })
      .then((dataUrl) => {
        if (dataUrl) {
          setEnergyLabelSrc(dataUrl);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [apiUrl, item.tyre.ean, auth.user?.access_token]);

  return (
    <Modal.Container open={true} onClose={onClose} size='xl' centered>
      <Modal.Header closeButton></Modal.Header>
      <Flex>
        <div className='bg-neutral-grey-2 p-4' style={{ width: '40%' }}>
          <ImageSlide
            images={[
              {
                src: item.tyre.image
                  ? `/common/tyres${item.tyre.image}`
                  : undefined,
                defaultImage: withCdnUrl('/img/tire-sample.png'),
                baseUrl: cdnUrl,
              },
              {
                src: energyLabelSrc,
                defaultImage: `data:image/png;base64,${DEFAULT_ENERGY_LABEL}`,
                baseUrl: undefined,
              },
            ]}
          />
        </div>
        <div className='flex-1 p-4'>
          <TyreInfoBox
            t={t}
            tyre={{
              brand: item.tyre.brand,
              dimension: item.tyre.dimension,
              ean: item.tyre.ean,
              profile: item.tyre.profile ?? '-',
              season: item.tyre.season,
              homologations: item.tyre.homologations,
              prodInfo: item.tyre.prodInfo,
              vehicleType: item.tyre.vehicleType,
              ...getTyreIdentifiers(
                {
                  cai: item.tyre.supplierCode,
                  constructorReference: item.tyre.constructorReference,
                  alternativeReference: item.tyre.alternativeReference,
                },
                customer.settingTemplate?.generalSetting.tyreIdentifierFields ??
                  undefined,
              ),
            }}
            imageBaseUrl={cdnUrl}
            mode='PARTIAL'
          />
          <div className='mt-4 mb-2'>
            {!isNil(item.tyre.bf) && (
              <Text color='secondary-gray' weight='bold' heading='xxs'>
                <Trans
                  i18nKey='app.purchase.quote_search_page.product.bf'
                  values={{
                    price: locale.formatters.money(item.tyre.bf, {
                      currency: currency,
                      fallback: '/',
                    }),
                  }}
                />
              </Text>
            )}
            <Flex className='gap-1' alignItems='center'>
              <Title heading='lg' weight='bold' color='tertiary-bold'>
                {item.containsFallbackOffers ? (
                  '-'
                ) : (
                  <Trans
                    i18nKey='app.purchase.quote_search_page.product.price_with_taxes'
                    values={{
                      price: locale.formatters.money(
                        item.pricing?.unitPriceTaxIncl,
                        {
                          currency: currency,
                        },
                      ),
                    }}
                  />
                )}
              </Title>
              {!isNil(item.pricing?.bfDiscount) &&
                !item.containsFallbackOffers &&
                item.pricing?.bfDiscount !== 0 && (
                  <Text color='secondary-gray' weight='bold' heading='xxs'>
                    <Trans
                      i18nKey='app.purchase.quote_search_page.product.bf_discount'
                      values={{
                        value: -item.pricing?.bfDiscount,
                      }}
                    />
                  </Text>
                )}
            </Flex>
          </div>
          <div className='my-4 border border-1' />
          <Text heading='sm' weight='bold' color='secondary'>
            <Trans i18nKey='app.purchase.quote_search_page.product.details.information' />
          </Text>
          <Flex className='mt-3'>
            <div className='flex-1 space-y-2'>
              <Row>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.common.product.width' />
                  </Text>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {item.tyre.width}
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.common.product.height' />
                  </Text>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {item.tyre.height}
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.common.product.diameter' />
                  </Text>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {item.tyre.diameter}
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.purchase.quote_search_page.product.details.load_index' />
                  </Text>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {item.tyre.loadIndex}
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.purchase.quote_search_page.product.details.speed_index' />
                  </Text>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {item.tyre.speedIndex}
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.common.product.ean' />
                  </Text>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {isEmptyValue(item.tyre.ean) ? '-' : item.tyre.ean}
                  </Text>
                </Col>
              </Row>
              {customer.settingTemplate?.generalSetting.tyreIdentifierFields?.includes(
                TYRE_IDENTIFIER_FIELD.CAI,
              ) &&
                TYRE_IDENTIFIER_FIELD.CAI &&
                item.tyre.supplierCode && (
                  <Row>
                    <Col>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        color='secondary-gray'
                      >
                        <Trans i18nKey='app.purchase.quote_search_page.product.details.cai' />
                      </Text>
                    </Col>
                    <Col>
                      <Text heading='xs' weight='semi-bold' color='secondary'>
                        {item.tyre.supplierCode}
                      </Text>
                    </Col>
                  </Row>
                )}
              {customer.settingTemplate?.generalSetting.tyreIdentifierFields?.includes(
                TYRE_IDENTIFIER_FIELD.CONSTRUCTOR_REFERENCE,
              ) &&
                TYRE_IDENTIFIER_FIELD.CONSTRUCTOR_REFERENCE &&
                item.tyre.constructorReference && (
                  <Row>
                    <Col>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        color='secondary-gray'
                      >
                        <Trans i18nKey='app.common.product.reference' />
                      </Text>
                    </Col>
                    <Col>
                      <Text heading='xs' weight='semi-bold' color='secondary'>
                        {item.tyre.constructorReference}
                      </Text>
                    </Col>
                  </Row>
                )}
              {customer.settingTemplate?.generalSetting.tyreIdentifierFields?.includes(
                TYRE_IDENTIFIER_FIELD.WYZ_REFERENCE,
              ) &&
                TYRE_IDENTIFIER_FIELD.WYZ_REFERENCE &&
                item.tyre.alternativeReference && (
                  <Row>
                    <Col>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        color='secondary-gray'
                      >
                        <Trans i18nKey='app.common.product.wyz_reference' />
                      </Text>
                    </Col>
                    <Col>
                      <Text heading='xs' weight='semi-bold' color='secondary'>
                        {item.tyre.alternativeReference}
                      </Text>
                    </Col>
                  </Row>
                )}
            </div>
            <div className='flex-1 space-y-2'>
              <Row>
                <Col sm={9}>
                  <Flex alignItems='center' className='gap-2'>
                    <Icon icon='showers-line' size='sm' />{' '}
                    <Text
                      heading='xs'
                      weight='semi-bold'
                      color='secondary-gray'
                    >
                      <Trans i18nKey='app.purchase.quote_search_page.product.details.breaking' />
                    </Text>
                  </Flex>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {isEmptyValue(item.tyre.wetGrip) ? '-' : item.tyre.wetGrip}
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col sm={9}>
                  <Flex alignItems='center' className='gap-2'>
                    <Icon icon='gas-station-line' size='sm' />{' '}
                    <Text
                      heading='xs'
                      weight='semi-bold'
                      color='secondary-gray'
                    >
                      <Trans i18nKey='app.purchase.quote_search_page.product.details.consumption' />
                    </Text>
                  </Flex>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {isEmptyValue(item.tyre.rollingResistance)
                      ? '-'
                      : item.tyre.rollingResistance}
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col sm={9}>
                  <Flex alignItems='center' className='gap-2'>
                    <Icon icon='volume-down-line' size='sm' />{' '}
                    <Text
                      heading='xs'
                      weight='semi-bold'
                      color='secondary-gray'
                    >
                      <Trans i18nKey='app.purchase.quote_search_page.product.details.noise' />
                    </Text>
                  </Flex>
                </Col>
                <Col>
                  <Text heading='xs' weight='semi-bold' color='secondary'>
                    {isEmptyValue(item.tyre.noisePerformance)
                      ? '-'
                      : item.tyre.noisePerformance}
                  </Text>
                </Col>
              </Row>
            </div>
          </Flex>
        </div>
      </Flex>
    </Modal.Container>
  );
}

export default DetailModal;
